const { queryFirst, queryAll } = require('../domUtil');
const { EVENT_KEY_ENTER, EVENT_KEY_SPACE, EVENT_KEY_DOWN, EVENT_KEY_UP, EVENT_KEY_ESC } = require('../constants');


export class AlertDialog {
    checkIcon = [
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">',
        '<path d="M1 6.4646L4.5 9.9646L11 3.4646" stroke="black"/>',
        '</svg>'
    ].join('\n');

    /**
     * Alert non-modal dialog used for showing messages to the user. Could be inserted
     * in different elements on the page. So it could be styled and positioned differently.
     * It has a timeout for auto-closing. Could be extened to use with Manager.
     *
     * @constructor
     * @param {AlertConfig} alertConfig - The configuration object for setting up the alert dialog.
     *
     * @typedef {Object} CtaItem
     * @property {string} text - The text displayed on the CTA button.
     * @property {Function} callback - The function to be executed when the CTA button is clicked.
     *
     * @typedef {Object} AlertItem
     * @property {string} text - The main text for the alert item.
     * @property {string[]} customCSSClass - An array of CSS classes applied to the alert item.
     * @property {Function} callback - A function executed when the alert item is clicked.
     * @property {CtaItem[]} cta - An array of call-to-action buttons, each with a text and callback.
     * @property {string} icon - HTML string representing an icon (e.g., an image) displayed with the alert item.
     *
     * @typedef {Object} AlertConfig
     * @property {number} alertTimeout - Optional. The time of displaying the alert until closed automatically. Default: 2500
     * @property {AlertItem[]} items - An array of alert items, each containing text, CSS classes, callback functions, CTAs, and an icon.
     * @property {Element} focusAfterClose - The DOM element that should regain focus after the alert dialog is closed.
     * @property {string[]} customCSSClass - An array of CSS classes applied to the alert dialog itself.
     * @property {string} style - Inline styles applied to the alert dialog for positioning or additional styling.
     * @property {boolean} remainOnScroll - Optional. Determines if alert keeps on showing when page is scrolled. Default: false
     *
     * @type {AlertConfig}
     * @example
     * const alertConfig = {
     *    alertTimeout: 2500,
     *    type: 'dialog',
     *    items: [
     *              {
     *                  text: 'Removed from Wishlist',
     *                  customCSSClass: [''],
     *                  callback: () => console.log('Alert clicked'),
     *                  cta: [
     *                          { text: 'OK', callback: () => console.log('OK clicked') },
     *                          { text: 'Cancel', callback: () => console.log('Cancel clicked') }
     *                        ],
     *                  icon: '<img src="product.png" alt="product-name" width=60 height=75 />'
     *              }
     *    ],
     *    focusAfterClose: queryFirst('.header-button'),
     *    customCSSClass: ['m-modifier'],
     *    style: '',
     *    remainOnScroll: false
     *  };
     *  new AlertDialog(alertConfig);
     */
    constructor(alertConfig) {
        this.alertHost = document.body;
        this.alertConfig = alertConfig;
        this.alert = null;
        this.timeout = null;
        this.state = { hovered: false, focused: false };
        this.createAlert();
    }

    createAlert() {
        if (!this.alertConfig) {
            console.error('Alert not configured');
            return;
        }
        const titleID = `alert-dialog-title-${this.generateRandomString()}`;
        const alert = document.createElement('div');
        const labelElement = document.createElement('h2');
        let firstOptionReg = false;
        const innerList = document.createElement('div');

        if (this.alertConfig.type == 'alert') {
            alert.setAttribute('role', 'alertdialog');
            alert.setAttribute('aria-modal', 'false');
            alert.setAttribute('tabindex', '0'); // non-modal pattern keeps tabindex on container
        } else if (this.alertConfig.type == 'dialog') {
            alert.setAttribute('role', 'dialog');
            alert.setAttribute('aria-modal', 'true');
        }

        alert.classList.add('alert-dialog');
        alert.setAttribute('aria-labelledby', titleID);
        alert.setAttribute('style', this.alertConfig.style || '');

        labelElement.classList.add('sr-only');
        labelElement.setAttribute('id', titleID);
        labelElement.textContent = this.alertConfig.labelText;
        alert.appendChild(labelElement);

        if (this.alertConfig.type == 'dialog') {
            innerList.setAttribute('role', 'listbox');
            innerList.setAttribute('aria-orientation', 'vertical');
            innerList.setAttribute('tabindex', '0');
            innerList.setAttribute('aria-labelledby', titleID);
            alert.appendChild(innerList);
        }

        if (this.alertConfig.customCSSClass?.length) {
            this.alertConfig.customCSSClass.forEach(cssClass => alert.classList.add(cssClass));
        }

        const { icon } = this.alertConfig;
        if (icon) {
            const iconElement = document.createElement('div');
            iconElement.classList.add('alert-dialog-icon');
            if (icon === 'default') {
                iconElement.innerHTML = this.checkIcon;
            } else {
                iconElement.innerHTML = icon;
            }

            alert.appendChild(iconElement);
        }

        if (this.alertConfig?.items) {
            let firstOptionReg = false;

            this.alertConfig.items.forEach(item => {
                // Build the row/option container
                const row = document.createElement('div');
                row.classList.add('alert-dialog-text');
                if (item.customCSSClass?.length) {
                    item.customCSSClass.forEach(c => row.classList.add(c));
                }
                if (item.attributes) {
                    Object.entries(item.attributes).forEach(([k, v]) => row.setAttribute(k, v));
                }

                // If dialog → row becomes an option inside the listbox
                const isDialog = this.alertConfig.type === 'dialog';
                if (isDialog) {
                    row.setAttribute('role', 'option');
                    if (!row.id) row.id = `opt_${this.generateRandomString()}`;
                }

                if (item.icon) {
                    const iconWrap = document.createElement('span');
                    iconWrap.classList.add('alert-inner-icon');
                    iconWrap.setAttribute('aria-hidden', 'true'); // decorative
                    if (typeof item.icon === 'string') {
                        iconWrap.innerHTML = item.icon;
                    } else {
                        iconWrap.appendChild(item.icon);
                    }
                    alert.appendChild(iconWrap);
                }

                // Visible label text (readable by SR)
                const labelSpan = document.createElement('span');
                labelSpan.classList.add('alert-option-label');
                labelSpan.textContent = item.text;
                row.appendChild(labelSpan);

                // Append to correct container
                if (isDialog) {
                    innerList.appendChild(row);
                } else {
                    alert.appendChild(row);
                }

                // Pointer selection (keeps your existing callback + close)
                row.addEventListener('click', () => {
                    if (typeof item.callback === 'function') item.callback();
                    this.closeAlert();
                });

                // CTAs must NOT be children of listbox; append to alert container
                if (item?.cta?.length) {
                    const ctaContainer = document.createElement('div');
                    ctaContainer.classList.add('alert-dialog-ctas');

                    item.cta.forEach(cta => {
                        const btn = document.createElement('button');
                        btn.type = 'button';
                        btn.textContent = cta.text;
                        if (cta.ariaLabel) btn.setAttribute('aria-label', cta.ariaLabel);
                        if (typeof cta.callback === 'function') {
                            btn.addEventListener('click', e => {
                                e.stopPropagation();
                                cta.callback();
                                this.closeAlert();
                            });
                        }
                        ctaContainer.appendChild(btn);
                    });

                    // Always outside the listbox to keep listbox children = only options
                    alert.appendChild(ctaContainer);
                }
            });
        }

        alert.addEventListener('pointerenter', () => {
            this.state.hovered = true;
            this.updateTimer();
        });

        alert.addEventListener('pointerleave', () => {
            // Leaving the alert altogether
            this.state.hovered = false;
            this.state.focused = false;
            this.updateTimer();
        });

        alert.addEventListener('focusin', () => {
            this.state.focused = true;
            this.updateTimer();
        });

        alert.addEventListener('focusout', (e) => {
            if (e.relatedTarget && alert.contains(e.relatedTarget)) return;

            setTimeout(() => {
                const stillInside = alert.contains(document.activeElement);
                if (!stillInside) {
                    this.state.focused = false;
                    this.updateTimer();
                }
            }, 0);
        });

        alert.addEventListener('keydown', (e) => {
            if (e.key === EVENT_KEY_ESC) {
                this.closeAlert();
            }
        });

        // In design, we have requirements to close the alert on scroll
        window.addEventListener('scroll', (this.handleScroll = () => {
            const engaged = this.state.hovered || this.state.focused;
            if (!this.alertConfig?.remainOnScroll && !engaged) {
                this.closeAlert();
            }
        }));

        this.alertHost.insertAdjacentElement('beforeend', alert); // FIFO
        this.alert = alert;

        if (this.alertConfig.type === 'dialog') {
            const listbox = queryFirst('[role="listbox"]', alert);
            if (listbox) {
                // Ensure options are valid for listbox
                const options = queryAll('[role="option"]', listbox);
                options?.forEach(opt => {
                    if (opt.hasAttribute('tabindex')) opt.removeAttribute('tabindex');
                    if (!opt.id) opt.id = `opt_${this.generateRandomString()}`;
                });

                // Focus the listbox so Arrow keys are intended for it
                listbox.focus({ preventScroll: true });

                listbox.addEventListener('keydown', () => {
                    // keep auto-close paused while keyboard navigating
                    this.state.focused = true;
                    this.updateTimer();
                }, { capture: true });

                // Bind robust key handling (global capture) and pointer selection
                this.bindListboxControls(listbox);
            }
        }
        this._kbMode = false;
        this.startTimer();
    }

    startTimer() {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => this.closeAlert(), this.alertConfig?.alertTimeout ?? 2500);
    }

    pauseTimer() {
        clearTimeout(this.timeout);
    }

    resumeTimer() {
        this.startTimer();
    }

    updateTimer = () => {
        if (this.state.hovered || this.state.focused) {
            clearTimeout(this.timeout);
        } else {
            this.startTimer();
        }
    };

    closeAlert() {
        if (!this.alert || this._isClosing) return;
        this._isClosing = true;
        this.unbindListboxControls?.();
        window.removeEventListener('scroll', this.handleScroll);
        clearTimeout(this.timeout);

        const el = this.alert;
        this.alert = null;

        if (el && el.parentNode) {
            el.classList.add('m-onclose');
            setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 150);
        }

        const ret = this.alertConfig?.focusAfterClose;
        if (ret instanceof HTMLElement) ret.focus();
    }

    generateRandomString() {
        return (Math.random() + 1).toString(36).substring(3);
    }

    bindListboxControls(listbox) {
        // Pointer selection (inside listbox)
        this._onListboxClick = (e) => {
            const opt = e.target.closest('[role="option"]');
            if (!opt) return;
            this.setActiveOption(listbox, opt);
            opt.click(); // reuse existing option click (invokes item.callback + closeAlert)
        };
        listbox.addEventListener('click', this._onListboxClick);

        // Global capture key handler: wins over bubble listeners elsewhere
        this._onGlobalKeydown = (e) => {
            // If dialog is gone, ignore
            if (!document.body.contains(this.alert)) return;

            const k = e.key;
            // Determine whether the event belongs to our listbox interaction
            const path = e.composedPath ? e.composedPath() : [];
            const targetInside = path.includes(listbox) ||
                document.activeElement === listbox ||
                listbox.contains(document.activeElement);

            if (!targetInside) return;

            this._kbMode = true;
            this.state.focused = true;
            this.updateTimer();

            if (k === EVENT_KEY_DOWN || k === EVENT_KEY_UP) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation?.();
                this.moveListboxActive(listbox, k);
            } else if (k === EVENT_KEY_ENTER || k === EVENT_KEY_SPACE) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation?.();
                this.selectListboxActive(listbox);
            }
        };
        window.addEventListener('keydown', this._onGlobalKeydown, { capture: true });

        this._onListboxPointerEnter = () => {
            const opts = Array.from(queryAll('[role="option"]', listbox));
            opts.forEach(o => o.removeAttribute('aria-selected'));
            listbox.removeAttribute('aria-activedescendant');
        };
        listbox.addEventListener('pointerenter', this._onListboxPointerEnter);
    }

    unbindListboxControls() {
        const listbox = queryFirst('[role="listbox"]', this.alert);
        if (listbox && this._onListboxClick) {
            listbox.removeEventListener('click', this._onListboxClick);
        }
        if (this._onGlobalKeydown) {
            window.removeEventListener('keydown', this._onGlobalKeydown, { capture: true });
        }
        if (listbox && this._onListboxPointerEnter) {
            listbox.removeEventListener('pointerenter', this._onListboxPointerEnter);
        }
        this._onListboxClick = null;
        this._onGlobalKeydown = null;
        this._onListboxPointerEnter = null;
    }

    // Keeps aria-activedescendant in sync; optionally updates aria-selected for keyboard
    setActiveOption(listbox, opt, updateSelected = false) {
        listbox.setAttribute('aria-activedescendant', opt.id);

        if (updateSelected) {
            const opts = Array.from(queryAll('[role="option"]', listbox));
            opts.forEach(o => o.setAttribute('aria-selected', String(o === opt)));
        }

        // If the list scrolls, keep active option in view
        opt.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    moveListboxActive(listbox, key) {
        const opts = Array.from(queryAll('[role="option"]', listbox));
        if (!opts.length) return;

        // If nothing is selected yet, initialize selection on first ArrowDown or last on ArrowUp
        const anySelected = opts.some(o => o.getAttribute('aria-selected') === 'true');
        if (!anySelected) {
            const target = (key === EVENT_KEY_UP) ? opts[opts.length - 1] : opts[0];
            this.setActiveOption(listbox, target, true);
            return;
        }

        const activeId = listbox.getAttribute('aria-activedescendant');
        let i = Math.max(0, opts.findIndex(o => o.id === activeId));

        if (key === EVENT_KEY_DOWN) i = (i + 1) % opts.length;
        else if (key === EVENT_KEY_UP) i = (i - 1 + opts.length) % opts.length;

        this.setActiveOption(listbox, opts[i], true);
    }


    selectListboxActive(listbox) {
        const id = listbox.getAttribute('aria-activedescendant');
        const opt = id && document.getElementById(id);
        if (opt) opt.click(); // triggers item.callback + closeAlert
    }
}
